import { useState, useCallback } from 'react'
import type { DiagnosisCode } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import { useAuth } from '../../hooks/use-auth'
import {
  PageHeader,
  DataTable,
  Pagination,
  ColumnSettingsButton,
  StatusBadge,
  btnPrimary,
  btnSecondary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { DiagnosisCodeEditModal } from './DiagnosisCodeEditModal'
import { BulkImportModal } from '../../components/BulkImportModal'
import { getAuthToken } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

export function DiagnosisCodeListPage() {
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<DiagnosisCode>({
    path: '/diagnosis-codes',
    filters: { search: debouncedSearch || undefined },
  })

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('diagnosis-codes')

  const columns: readonly Column<DiagnosisCode>[] = [
    { key: 'code', header: 'Code', sortable: true, filterable: true, render: (d) => <strong>{d.code}</strong> },
    { key: 'desc', header: 'Description', filterable: true, render: (d) => d.description },
    { key: 'parent', header: 'Parent', sortable: true, render: (d) => d.parentCode || '--' },
    { key: 'billable', header: 'Billable', render: (d) => (d.isBillable ? 'Yes' : 'No') },
    {
      key: 'isSystem',
      header: 'System',
      render: (d) => (d.isSystem ? <StatusBadge status="in_progress" label="System" /> : null),
    },
    { key: 'status', header: 'Status', sortable: true, render: (d) => (d.isActive ? 'Active' : 'Archived') },
  ]

  const handleExportCsv = useCallback(() => {
    const params = new URLSearchParams({ format: 'csv' })
    if (debouncedSearch) params.set('search', debouncedSearch)
    const url = `/api/bulk/diagnosis_code/export?${params.toString()}`
    const token = getAuthToken()
    void fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (res) => {
      if (!res.ok) return
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const filename = match ? match[1] : `diagnosis_code-${new Date().toISOString().slice(0, 10)}.csv`
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      link.click()
      URL.revokeObjectURL(objectUrl)
    })
  }, [debouncedSearch])

  return (
    <div>
      <PageHeader
        title="Diagnosis Codes (ICD-10)"
        subtitle={`${total} total`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button
              type="button"
              onClick={() => {
                setSelectedId(null)
                setIsNew(true)
                setModalOpen(true)
              }}
              style={btnPrimary}
            >
              Add Code
            </button>
            {user?.role !== 'tech' && (
              <button type="button" onClick={() => setImportModalOpen(true)} style={btnSecondary}>
                Import CSV
              </button>
            )}
            <button type="button" onClick={handleExportCsv} style={btnSecondary}>
              Export CSV
            </button>
          </div>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '320px' }}
          placeholder="Search by code or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(d) => {
              setSelectedId(d.id)
              setIsNew(false)
              setModalOpen(true)
            }}
            emptyMessage="No diagnosis codes yet. Add or import the ICD-10 master list."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}
      <DiagnosisCodeEditModal
        codeId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => refresh()}
        onArchived={() => refresh()}
      />

      <BulkImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        entityType="diagnosis_code"
        entityLabel="Diagnosis Codes"
        onSuccess={refresh}
      />
    </div>
  )
}
